import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { useMaterialUIController } from "context";
import useStock from "hooks/useStock";
import CustomPagination from "examples/CustomPagination/CustomPagination";
import stockTableData from "layouts/stock/data/stockTableData";
import MDButton from "components/MDButton";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import theme from "assets/theme";
import { debounce } from "lodash";
import {
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import useDashboard from "hooks/useDashboard";
function Tables() {
  const { getstockList, stockList, stockPreview, stockSync } = useStock();
  const { columns, rows } = stockTableData();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;
  const fullScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const { searchTerm } = useDashboard();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPage, setTotalPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [error, setError] = useState("");
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [distributor, setDistributor] = useState("");

  // Function to fetch dealers
  const fetchStock = () => {
    const query = searchTerm.length >= 3 ? searchTerm : ""; // Apply search only if length >= 3
    getstockList({ limit, page, search: query });
  };

  // Debounced search function
  const debouncedfetchStock = debounce(fetchStock, 500);

  // Initial Load (Mandatory)
  useEffect(() => {
    fetchStock(); // Always fetch initial data on mount
  }, []);

  // Search & Pagination Updates
  useEffect(() => {
    if (searchTerm.length === 0 || searchTerm.length >= 3) {
      debouncedfetchStock();
    }
    return () => debouncedfetchStock.cancel(); // Cleanup debounce
  }, [searchTerm, page, limit]);

  useEffect(() => {
    if (stockList.page && stockList.limit) {
      setPage(stockList.page);
      setLimit(stockList.limit);
      setTotalPage(stockList.pages || 1);
      setTotal(stockList.total || 0);
    }
  }, [stockList]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    getstockList({ limit: 10, page: newPage });
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
    setSelectedFile(null);
    setError("");
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFile(null);
    setError("");
  };

  const handleClosePreviewDialog = () => {
    setOpenPreviewDialog(false);
    setPreviewData([]);
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];

    if (file) {
      const validMimeTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      const validExtensions = [".xlsx", ".xls"];

      const fileExtension = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2);

      if (!validMimeTypes.includes(file.type) || !validExtensions.includes(`.${fileExtension}`)) {
        setError("Invalid file type. Please upload an Excel file (.xls, .xlsx).");
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError("");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".xls,.xlsx",
    multiple: false,
  });

  const handleConfirm = async () => {
    const stock = previewData
      .filter((item) => item.selected)
      .map(({ selected, id, ...rest }) => rest); // Remove 'selected' key

    const payload = { distributor, stock };

    try {
      const res = await stockSync(payload);
      if (res.success) {
        alert("Stock synced successfully");
        handleClosePreviewDialog();
        fetchStock();
      } else {
        alert(res.message || "Something went wrong!");
        setPreviewData(
          res?.error?.stock.map((item) => ({
            ...item,
            selected: item?.warehouseExist && item?.productExist,
          }))
        );
        setDistributor(res?.error?.distributor);
      }
    } catch (error) {
      console.error("Stock sync error:", error);
      alert("An error occurred while syncing stock");
    }
  };

  const handleCheckboxChange = (index) => {
    const updatedData = [...previewData];
    updatedData[index].selected = !updatedData[index].selected;
    setPreviewData(updatedData);
  };

  const handleInputChange = (index, field, value) => {
    setPreviewData((prevData) => {
      return prevData.map((item, i) =>
        i === index
          ? {
              ...item,
              [field]: value,
              ...(field === "product" && { productExist: true }),
              ...(field === "warehouse" && { warehouseExist: true }),
            }
          : item
      );
    });
  };

  const handleFileUpdate = () => {
    if (selectedFile) {
      const formData = new FormData();
      formData.append("file", selectedFile);

      stockPreview(formData)
        .then((response) => {
          if (response.success) {
            setDistributor(response.data.distributor);
            setPreviewData(response.data.preview.map((item) => ({ ...item, selected: true })));
            setOpenPreviewDialog(true);
          } else {
            window.alert(response.message || "Something went wrong!");
          }
          handleCloseDialog();
        })
        .catch((error) => {
          console.error("Error uploading file:", error);
          window.alert("Failed to upload and sync file. Please try again.");
        });

      setOpenDialog(false);
    } else {
      window.alert("Please select a file before updating.");
    }
  };

  const handleSelectAll = () => {
    const allSelected = previewData.every((item) => item.selected);
    const updatedData = previewData.map((item) => ({
      ...item,
      selected: !allSelected,
    }));
    setPreviewData(updatedData);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={2}
                px={2}
                variant="gradient"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgColor={sidenavColor}
                borderRadius="lg"
                coloredShadow={sidenavColor}
              >
                <MDTypography variant="h6" color="white">
                  Stock Table
                </MDTypography>
                <MDButton variant="contained" onClick={handleOpenDialog}>
                  Update & Sync File
                </MDButton>
              </MDBox>
              <MDBox pt={3}>
                <DataTable
                  table={{ columns, rows }}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              </MDBox>
            </Card>
            <MDBox
              sx={{ width: "100%", display: "flex", justifyContent: "start", marginTop: "1rem" }}
            >
              <CustomPagination
                totalCount={totalPage}
                currentPage={page}
                onPageChange={handlePageChange}
              />
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      {/* Drag & Drop File Upload Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>File Update & Sync</DialogTitle>
        <DialogContent>
          <MDBox
            {...getRootProps()}
            sx={{
              border: "2px dashed #999",
              borderRadius: "8px",
              padding: "20px",
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: isDragActive ? "#f0f0f0" : "#fff",
            }}
          >
            <input {...getInputProps()} />
            <CloudUploadIcon sx={{ fontSize: 150, color: "#999" }} />
            <MDTypography sx={{ mt: 2 }}>
              {isDragActive
                ? "Drop the file here..."
                : "Drag & drop an Excel file here, or click to browse"}
            </MDTypography>
          </MDBox>
          {selectedFile && (
            <MDTypography sx={{ mt: 2, color: "green" }}>
              Selected file: {selectedFile.name}
            </MDTypography>
          )}
          {error && (
            <MDTypography color="error" sx={{ mt: 1 }}>
              {error}
            </MDTypography>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleCloseDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleFileUpdate} color="info" disabled={!selectedFile}>
            Update & Sync
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openPreviewDialog}
        onClose={handleClosePreviewDialog}
        fullScreen={fullScreen}
        aria-labelledby="responsive-dialog-title"
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            overflow: "hidden", // Hide scroll
          },
          "& .MuiDialogContent-root": {
            overflow: "hidden", // Ensure content doesn't overflow
          },
        }}
      >
        <DialogTitle>{distributor} - Stock Sync Preview</DialogTitle>
        <DialogContent>
          <TableContainer
            component={Paper}
            sx={{ maxHeight: 500, overflowY: "auto", borderRadius: 2 }}
          >
            `{" "}
            <Table>
              <TableRow>
                <TableCell>
                  {" "}
                  <Checkbox
                    indeterminate={
                      previewData?.some((item) => item.selected) &&
                      !previewData?.every((item) => item.selected)
                    }
                    checked={previewData.every((item) => item.selected)}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Warehouse</TableCell>
                <TableCell>Quantity</TableCell>
              </TableRow>
              <TableBody>
                {previewData.map((row, index) => {
                  const isWarehouseWrong = row.warehouseExist === false;
                  const isProductNameWrong = row.productExist === false;
                  const isWrong = isWarehouseWrong || isProductNameWrong;
                  return (
                    <TableRow
                      key={index}
                      hover
                      sx={{ backgroundColor: isWrong ? "#ffebee" : "inherit" }} // Light red background for low stock
                    >
                      <TableCell>
                        <Checkbox
                          checked={row.selected}
                          onChange={() => handleCheckboxChange(index)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.product}
                          onChange={(e) => handleInputChange(index, "product", e.target.value)}
                          sx={{
                            border: isWrong && isProductNameWrong && "2px solid red",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.warehouse}
                          onChange={(e) => handleInputChange(index, "warehouse", e.target.value)}
                          sx={{
                            border: isWrong && isWarehouseWrong && "2px solid red",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={row.quantity}
                          onChange={(e) => handleInputChange(index, "quantity", e.target.value)}
                          inputProps={{ min: 0 }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            `
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClosePreviewDialog} color="dark">
            Cancel
          </MDButton>
          <MDButton onClick={handleConfirm} color="info">
            Confirm
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tables;
